// src/components/auth/RegisterModal.jsx
import * as React from 'react';
import {
  Dialog, DialogContent, Paper, Box, Typography, TextField, Button,
  Alert, Link, IconButton, Menu, MenuItem, Stack
} from '@mui/material';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { useNavigate, useLocation } from 'react-router-dom';
import { api } from '../../lib/api';
import { saveAuth } from '../../lib/auth';
import FancyToast from '../common/FancyToast';

import { useI18n } from '../../i18n/I18nProvider';
import registerI18n from '../../i18n/pages/auth/register.i18n';

import { LANGS } from '../../i18n/langs';

const BLUE = '#0466c8';
const BLUE_HOVER = '#004890';

const textFieldSx = {
  '& label.Mui-focused': { color: BLUE },
  '& .MuiOutlinedInput-root': {
    '& fieldset': { borderColor: '#ccc' },
    '&:hover fieldset': { borderColor: BLUE },
    '&.Mui-focused fieldset': { borderColor: BLUE },
  },
  '& .MuiFormHelperText-root': { mt: 0.25 },
};

// === Levenshtein pour détecter gmai.com → gmail.com, etc.
function levenshtein(a = '', b = '') {
  a = a.toLowerCase(); b = b.toLowerCase();
  const m = Array.from({ length: a.length + 1 }, () => new Array(b.length + 1).fill(0));
  for (let i = 0; i <= a.length; i++) m[i][0] = i;
  for (let j = 0; j <= b.length; j++) m[0][j] = j;
  for (let i = 1; i <= a.length; i++) {
    for (let j = 1; j <= b.length; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      m[i][j] = Math.min(m[i - 1][j] + 1, m[i][j - 1] + 1, m[i - 1][j - 1] + cost);
    }
  }
  return m[a.length][b.length];
}

const KNOWN_EMAIL_DOMAINS = [
  'gmail.com', 'yahoo.com', 'outlook.com', 'hotmail.com', 'live.com', 'icloud.com',
  'free.fr', 'orange.fr', 'sfr.fr', 'laposte.net',
  'yahoo.fr', 'outlook.fr', 'hotmail.fr'
];

function validateEmailWithDomain(email) {
  const res = { ok: true, suggestion: null, errorKey: null };
  const value = String(email || '').trim();
  const basic = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
  if (!basic) return { ok: false, suggestion: null, errorKey: 'register.errors.emailInvalid' };

  const [, domain = ''] = value.split('@');
  const d = domain.toLowerCase();
  if (KNOWN_EMAIL_DOMAINS.includes(d)) return res;

  let best = null;
  for (const k of KNOWN_EMAIL_DOMAINS) {
    const dist = levenshtein(d, k);
    if (dist <= 2) best = !best || dist < best.dist ? { k, dist } : best;
  }
  if (best) return { ok: false, suggestion: best.k, errorKey: 'register.errors.emailDomainTypo' };
  return res;
}

export default function RegisterModal({ open, onClose, onRegistered, onOpenLogin, forceRedirect = false }) {
  const { t, register, lang, setLang } = useI18n();
  React.useEffect(() => { register('register', registerI18n); }, [register]);

  const nav = useNavigate();
  const location = useLocation();

  const [form, setForm] = React.useState({
    email: '',
    companyName: '',
    password: '',
    confirm: '',
    phone: '',
  });
  const [err, setErr]   = React.useState('');
  const [ok, setOk]     = React.useState('');
  const [loading, setLoading] = React.useState(false);
  const [langAnchor, setLangAnchor] = React.useState(null);
  const [toastOpen, setToastOpen] = React.useState(false);
  const [emailFieldErr, setEmailFieldErr] = React.useState('');

  const currentLang = LANGS.find(l => l.code === lang) || LANGS[0];

  const handleChange = (e) => {
    const { name, value } = e.target;

    // 📞 Normalisation téléphone : uniquement chiffres + un éventuel "+" en début
    if (name === 'phone') {
      let cleaned = String(value || '').replace(/[^0-9+]/g, '');

      // On autorise un seul "+" et uniquement en première position
      const plusIndex = cleaned.indexOf('+');
      if (plusIndex > 0) {
        // "+" pas en première position → on enlève tous les "+"
        cleaned = cleaned.replace(/\+/g, '');
      } else if (plusIndex === 0) {
        // "+" au début → on garde le premier, on supprime les autres
        cleaned = '+' + cleaned.slice(1).replace(/\+/g, '');
      } else {
        // aucun "+" → rien à faire de plus
      }

      setForm((f) => ({ ...f, phone: cleaned }));
      return;
    }

    setForm((f) => ({ ...f, [name]: value }));

    if (name === 'email') {
      if (!value) return setEmailFieldErr('');
      const { ok, suggestion, errorKey } = validateEmailWithDomain(value);
      if (!ok) {
        if (errorKey === 'register.errors.emailInvalid') {
          setEmailFieldErr(t('register.errors.emailInvalid'));
        } else if (errorKey === 'register.errors.emailDomainTypo' && suggestion) {
          setEmailFieldErr(t('register.errors.emailDomainTypo', { suggestion }));
        } else {
          setEmailFieldErr(t('register.errors.emailInvalid'));
        }
      } else {
        setEmailFieldErr('');
      }
    }
  };

  const autoRedirect = (data) => {
    const role = data?.user?.role;
    const redirectTo = location.state?.redirectTo;
    const pendingSummary = location.state?.pendingSummary;

    let stored = null;
    try { stored = JSON.parse(sessionStorage.getItem('redirectAfterAuth') || 'null'); } catch {}
    const target = redirectTo || stored?.redirectTo;
    const summary = pendingSummary || stored?.summary;

    if (target) {
      try { sessionStorage.removeItem('redirectAfterAuth'); } catch {}
      return nav(target, { replace: true, state: { summary } });
    }
    if (role === 'admin' || role === 'superadmin') return nav('/admin', { replace: true });
    return nav('/mes-envois', { replace: true });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErr(''); setOk('');

    const emailCheck = validateEmailWithDomain(form.email);
    if (!emailCheck.ok) {
      if (emailCheck.errorKey === 'register.errors.emailInvalid') setErr(t('register.errors.emailInvalid'));
      else if (emailCheck.errorKey === 'register.errors.emailDomainTypo' && emailCheck.suggestion) setErr(t('register.errors.emailDomainTypoBlock', { suggestion: emailCheck.suggestion }));
      else setErr(t('register.errors.emailInvalid'));
      return;
    }

    if (!form.email || !form.password || !form.phone) {
      return setErr(t('register.errors.requiredFields'));
    }
    if (form.password.length < 6) return setErr(t('register.errors.passwordLen'));
    if (form.password !== form.confirm) return setErr(t('register.errors.confirmMismatch'));

    setLoading(true);
    try {
      await api.post('/auth/register', {
        email: form.email,
        companyName: form.companyName || undefined,
        password: form.password,
        phone: form.phone || undefined,  // ✅ envoyé au back
      });

      const { data } = await api.post('/auth/login', {
        email: form.email,
        password: form.password,
      });
      saveAuth(data);

      setOk(t('register.success.accountCreated'));
      setToastOpen(true);

      const finalize = () => {
        if (onRegistered && !forceRedirect) { onRegistered(data); onClose?.(); return; }
        autoRedirect(data); onClose?.();
      };
      setTimeout(finalize, 900);
    } catch (error) {
      setErr(error?.response?.data?.message || t('register.errors.createFailed'));
    } finally {
      setLoading(false);
    }
  };

  const HELPER_MIN = 16;

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        fullWidth
        maxWidth="sm"
        BackdropProps={{ sx: { backgroundColor: 'rgba(0,0,0,0.35)', backdropFilter: 'blur(4px)' } }}
        PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden' } }}
      >
        <DialogContent sx={{ p: 0 }}>
          <Paper elevation={0} sx={{ p: { xs: 2.25, sm: 3 }, borderRadius: 0, position: 'relative', overflow: 'hidden' }}>
            <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, height: 6, bgcolor: BLUE }} />

            {/* Langue */}
            <Box sx={{ position: 'absolute', top: 10, right: 10 }}>
              <IconButton onClick={(e) => setLangAnchor(e.currentTarget)} size="small" sx={{ p: 0.5, borderRadius: 2, border: '1px solid #e0e0e0' }}>
                <Box component="img" src={currentLang.flag} alt={currentLang.alt} sx={{ height: 18, width: 'auto', mr: 0.5 }} />
                <ArrowDropDownIcon fontSize="small" sx={{ color: 'text.secondary' }} />
              </IconButton>
              <Menu
                anchorEl={langAnchor}
                open={Boolean(langAnchor)}
                onClose={() => setLangAnchor(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              >
                {LANGS.map((l) => (
                  <MenuItem
                    key={l.code}
                    onClick={() => { setLang(l.code); setLangAnchor(null); }}
                    selected={l.code === lang}
                    sx={{ minWidth: 48, justifyContent: 'center', py: 0.5 }}
                  >
                    <Box component="img" src={l.flag} alt={l.alt} sx={{ height: 18, width: 'auto' }} />
                  </MenuItem>
                ))}
              </Menu>
            </Box>

            <Typography component="h1" sx={{ fontWeight: 800, fontSize: { xs: 21, md: 24 }, mb: 0.25 }}>
              {t('register.title')}
            </Typography>
            <Typography sx={{ color: 'text.secondary', mb: 1.25 }}>
              {t('register.subtitle')}
            </Typography>

            {err && <Alert severity="error" sx={{ mb: 1 }}>{err}</Alert>}
            {ok  && <Alert severity="success" sx={{ mb: 1 }}>{ok}</Alert>}

            <Box component="form" onSubmit={handleSubmit} noValidate>
              <Stack spacing={1.1}>
                <TextField
                  name="email"
                  label={t('register.form.email')}
                  type="email"
                  fullWidth
                  required
                  size="small"
                  margin="none"
                  value={form.email}
                  onChange={handleChange}
                  sx={textFieldSx}
                  error={!!emailFieldErr}
                  helperText={emailFieldErr || ' '}
                  FormHelperTextProps={{ sx: { minHeight: HELPER_MIN } }}
                  disabled={loading}
                />

                <TextField
                  name="companyName"
                  label={t('register.form.company')}
                  fullWidth
                  size="small"
                  margin="none"
                  value={form.companyName}
                  onChange={handleChange}
                  sx={textFieldSx}
                  helperText={' '}
                  FormHelperTextProps={{ sx: { minHeight: HELPER_MIN } }}
                  disabled={loading}
                />

                {/* 📞 Téléphone */}
                <TextField
                  name="phone"
                  label={t('register.form.phone')}
                  type="tel"
                  inputMode="tel"
                  fullWidth
                  required
                  size="small"
                  margin="none"
                  value={form.phone}
                  onChange={handleChange}
                  sx={textFieldSx}
                  helperText="Ex : +33612345678"
                  FormHelperTextProps={{ sx: { minHeight: HELPER_MIN } }}
                  disabled={loading}
                />

                <TextField
                  name="password"
                  label={t('register.form.password')}
                  type="password"
                  fullWidth
                  required
                  size="small"
                  margin="none"
                  value={form.password}
                  onChange={handleChange}
                  helperText={t('register.form.minlen')}
                  FormHelperTextProps={{ sx: { minHeight: HELPER_MIN } }}
                  sx={textFieldSx}
                  disabled={loading}
                />

                <TextField
                  name="confirm"
                  label={t('register.form.confirm')}
                  type="password"
                  fullWidth
                  required
                  size="small"
                  margin="none"
                  value={form.confirm}
                  onChange={handleChange}
                  helperText={' '}
                  FormHelperTextProps={{ sx: { minHeight: HELPER_MIN } }}
                  sx={textFieldSx}
                  disabled={loading}
                />

                <Button
                  type="submit"
                  variant="contained"
                  disabled={loading}
                  fullWidth
                  sx={{ py: 1.05, fontWeight: 800, textTransform: 'none', bgcolor: BLUE, '&:hover': { bgcolor: BLUE_HOVER } }}
                >
                  {loading ? t('register.form.loading') : t('register.form.submit')}
                </Button>

                <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                  <Typography sx={{ color: 'text.secondary', mr: 1 }}>
                    {t('register.login.prompt')}
                  </Typography>
                  <Link
                    component="button"
                    type="button"
                    underline="hover"
                    onClick={() => onOpenLogin?.()}
                    sx={{ fontWeight: 700, color: BLUE }}
                  >
                    {t('register.login.link')}
                  </Link>
                </Box>
              </Stack>
            </Box>
          </Paper>
        </DialogContent>
      </Dialog>

      <FancyToast
        open={toastOpen}
        onClose={() => setToastOpen(false)}
        kind="celebration"
        titleKey="fancyToast.register.title"
        subtitleKey="fancyToast.register.subtitle"
        autoHideDuration={1200}
      />
    </>
  );
}
